import React, { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
    Button,
    Dialog,
    Divider,
    DialogTitle,
    DialogContent,
    DialogActions,
    ListItemIcon,
    IconButton,
    Menu,
    MenuItem,
    Typography
} from "@mui/material/";
import CloseIcon from "@mui/icons-material/Close";
import { strings } from "../../../common/i18n";
import { csv } from "../../../common/lib";
import optionsItem from "./optionsItem";
import actions from "../../../Store/actions";
import styles from "./styles";
import { logToSentry } from "../../../config/Sentry";

/**
 * Data Grid Pro Options Component
 */
export default ({
    columns,
    csvFileName,
    data,
    dataItem,
    error,
    header,
    FormComponent,
    onCsvMap = (item) => item,
    onCsvUpload,
    onDataItemAdd,
    onDataItemClear,
    onDataMap = (item) => item,
    onDataSave,
    optionOpen,
    onMenuClose,
    customOptions = []
}) => {
    const [csvData, setCsvData] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [modal, setModal] = useState(false);

    const csvColumns = columns.filter((column) => column.csv !== false);
    const dispatch = useDispatch();

    /** Actions for options  menu */
    const actionsMap = {
        onAdd: () => {
            try {
                onDataItemAdd && onDataItemAdd();
                onMenuClose();
            } catch (error) {
                const errStr = error.toString();
                logToSentry("On add data item:", { error: errStr });
                console.error(errStr);
                dispatch(
                    actions.toast.create.error(strings("errors.onAddItem"))
                );
            }
        },
        onDownload: () => {
            try {
                data.length &&
                    csv.download(csvColumns, data.map(onDataMap), csvFileName);
                onMenuClose();
            } catch (error) {
                const errStr = error.toString();
                console.error(errStr);
                logToSentry("On CSV download:", { error: errStr });
                dispatch(
                    actions.toast.create.error(strings("errors.onDownload"))
                );
            }
        },
        onTemplate: () => {
            try {
                csv.download(csvColumns, [], `${csvFileName}-template`);
                onMenuClose();
            } catch (error) {
                const errStr = error.toString();
                console.error(errStr);
                logToSentry("On CSV template download:", { error: errStr });
                dispatch(
                    actions.toast.create.error(strings("errors.onDownload"))
                );
            }
        },
        onUpload: async () => {
            try {
                const next = await csv.upload(csvColumns);
                onCsvUpload && onCsvUpload();
                if (next.length > 0) {
                    setCsvData(next.map(onCsvMap));
                } else {
                    setUploadError(strings("messages.uploadNoRows"));
                }
            } catch (error) {
                logToSentry("On CSV upload:", { error });
                setUploadError(error);
                const errStr = error.toString();
                console.error(errStr);
                dispatch(
                    actions.toast.create.error(
                        strings("errors.onCsvDataUpload")
                    )
                );
            }
            setModal(true);
            onMenuClose();
        }
    };
    /** On csv data clear */
    const onCsvDataClear = () => {
        try {
            setModal(false);
            setCsvData(null);
            setUploadError(null);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On CSV data clear:", { error: errStr });
            dispatch(
                actions.toast.create.error(strings("errors.onCsvDataClear"))
            );
        }
    };
    /** On csv data save */
    const onCsvDataSave = async () => {
        try {
            await onDataSave(csvData);
            setCsvData(null);
            setModal(false);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On CSV data save:", { error: errStr });
            dispatch(
                actions.toast.create.error(strings("errors.onCsvDataSave"))
            );
        }
    };
    /** On dialog for forms  */
    const onDialogClose = () => onDataItemClear && onDataItemClear();

    /** On csv re upload */
    const onReupload = () => {
        try {
            actionsMap.onUpload();
            setModal(false);
            setCsvData(null);
            setUploadError(null);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On CSV reupload:", { error: errStr });
            dispatch(
                actions.toast.create.error(strings("errors.onCsvReupload"))
            );
        }
    };

    /** Options to show */
    const options = useMemo(() => {
        if (customOptions.length > 0) {
            const optionList = [];
            optionsItem.map((item) => {
                if (customOptions.includes(item.label.toLowerCase()))
                    optionList.push(item);
            });
            return optionList;
        } else return optionsItem;
    }, [optionsItem, customOptions]);

    const err = error || uploadError ? true : false;

    return (
        <>
            <Menu
                anchorEl={optionOpen}
                open={Boolean(optionOpen)}
                onClose={onMenuClose}
            >
                {options.map((item) => (
                    <MenuItem
                        onClick={actionsMap[item.action]}
                        key={item.label}
                    >
                        <ListItemIcon>
                            <item.Icon color="primary" />
                        </ListItemIcon>
                        {strings(item.label)}
                    </MenuItem>
                ))}
            </Menu>
            <Dialog open={modal}>
                <DialogTitle>{strings("uploads")}</DialogTitle>
                <Divider />
                <DialogContent>
                    {uploadError && (
                        <Typography component="div" key="uploaod_error_list">
                            {uploadError.split("\n").map((line, idx) => (
                                <Typography
                                    key={`${idx}_${line}`}
                                    color={idx === 1 ? "primary" : "error"}
                                >
                                    {line}
                                </Typography>
                            ))}
                        </Typography>
                    )}
                    {csvData && (
                        <>
                            {err ? (
                                csvData.map((item, index) => (
                                    <Typography
                                        component="div"
                                        key={`${index}_csv_error_list}`}
                                    >
                                        {item.errors.length > 0 ? (
                                            <Typography
                                                key={`csv_error_${item.errors[0].props.row}`}
                                                color="error"
                                            >
                                                {strings("row")}{" "}
                                                {item.errors[0].props.row}:{" "}
                                                {item.errors[0].props.error}
                                            </Typography>
                                        ) : null}
                                    </Typography>
                                ))
                            ) : (
                                <Typography>
                                    {strings("update")}{" "}
                                    <Typography
                                        component="span"
                                        color="primary"
                                    >
                                        {csvData.length}
                                    </Typography>{" "}
                                    {strings("rowsToTheTable")} ?
                                </Typography>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={onCsvDataClear}
                    >
                        {strings("cancel")}
                    </Button>
                    {err ? (
                        <Button
                            variant="outlined"
                            color="success"
                            onClick={onReupload}
                        >
                            {strings("upload")}
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            color="success"
                            onClick={onCsvDataSave}
                        >
                            {strings("save")}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            {FormComponent && Boolean(dataItem) && (
                <Dialog open onClose={onDialogClose}>
                    <DialogTitle sx={{ m: 0, p: 2 }}>{header}</DialogTitle>
                    <IconButton onClick={onDialogClose} sx={styles.closeIcon}>
                        <CloseIcon />
                    </IconButton>
                    <DialogContent>
                        <FormComponent onSubmit={onDialogClose} />
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};
